import React, { useMemo, useState } from 'react';
import { Button, Card, CardContent, CardHeader, CardTitle, Input, Label, cn } from '@mf-tools/domains-ui';
import { z } from 'zod';
import { Container, Textarea } from '../components';
import { isErr } from '@mf-tools/types';

const contactSchema = z.object({
  name: z.string().min(1, 'Name is required').max(120, 'Name is too long'),
  email: z.string().min(1, 'Email is required').email('Enter a valid email').max(200, 'Email is too long'),
  company: z.string().max(200, 'Company is too long').optional(),
  message: z.string().min(1, 'Message is required').max(2000, 'Message is too long'),
  honey: z.string().optional(),
});

type ContactFormState = z.infer<typeof contactSchema>;
type FieldErrors = Partial<Record<keyof ContactFormState, string>>;

export interface ContactProps {
  className?: string;
}

export function Contact({ className }: ContactProps): React.ReactElement {
  const [form, setForm] = useState<ContactFormState>({
    name: '',
    email: '',
    company: '',
    message: '',
    honey: '',
  });
  const [errors, setErrors] = useState<FieldErrors>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [status, setStatus] = useState<{ type: 'idle' | 'success' | 'error'; message?: string }>({ type: 'idle' });

  const requiredMark = useMemo(() => <span className="text-destructive">*</span>, []);

  function setField<K extends keyof ContactFormState>(key: K, value: ContactFormState[K]): void {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function onSubmit(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setStatus({ type: 'idle' });

    const parsed = contactSchema.safeParse(form);
    if (!parsed.success) {
      const nextErrors: FieldErrors = {};
      for (const issue of parsed.error.issues) {
        const field = issue.path[0] as keyof ContactFormState | undefined;
        if (field && !nextErrors[field]) nextErrors[field] = issue.message;
      }
      setErrors(nextErrors);
      return;
    }

    setErrors({});

    // Honeypot: if a bot fills it, pretend success and do nothing.
    if ((parsed.data.honey ?? '').trim().length > 0) {
      setStatus({ type: 'success', message: 'Thanks — we’ll be in touch.' });
      setForm({ name: '', email: '', company: '', message: '', honey: '' });
      return;
    }

    setIsSubmitting(true);
    try {
      // Lazy-load the Firebase/Firestore submission logic for better initial load performance.
      const { submitContactRequest } = await import('../lib/contact-requests');
      const result = await submitContactRequest({
        name: parsed.data.name,
        email: parsed.data.email,
        company: parsed.data.company,
        message: parsed.data.message,
      });

      if (isErr(result)) {
        setStatus({ type: 'error', message: result.error });
        return;
      }

      setStatus({ type: 'success', message: 'Thanks — we’ll be in touch.' });
      setForm({ name: '', email: '', company: '', message: '', honey: '' });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <section id="contact" className={cn('py-14 sm:py-20', className)} aria-labelledby="contact-title">
      <Container>
        <div className="grid gap-10 lg:grid-cols-2 lg:items-start">
          <div className="space-y-4">
            <h2 id="contact-title" className="text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
              Contact
            </h2>
            <p className="text-base leading-relaxed text-muted-foreground sm:text-lg">
              Tell us what you’re trying to operationalize — we’ll show you how OpsPilott can replace tool sprawl with
              outcomes.
            </p>
            <div className="rounded-lg border bg-background p-4 text-sm text-muted-foreground">
              <div className="font-medium text-foreground">What happens next</div>
              <ul className="mt-2 grid gap-2">
                <li className="flex items-start gap-2">
                  <span className="mt-1 h-1.5 w-1.5 rounded-full bg-primary" aria-hidden="true" />
                  We respond within 1–2 business days
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-1 h-1.5 w-1.5 rounded-full bg-primary" aria-hidden="true" />
                  Short call to understand your ops stack and pain points
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-1 h-1.5 w-1.5 rounded-full bg-primary" aria-hidden="true" />
                  A tailored walkthrough (no vendor chaos)
                </li>
              </ul>
            </div>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Send a message</CardTitle>
            </CardHeader>
            <CardContent>
              <form className="space-y-4" onSubmit={onSubmit} noValidate>
                <div className="hidden">
                  <Label htmlFor="company_website">Company website</Label>
                  <Input
                    id="company_website"
                    name="company_website"
                    autoComplete="off"
                    tabIndex={-1}
                    value={form.honey ?? ''}
                    onChange={(e) => setField('honey', e.target.value)}
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="name">
                    Name {requiredMark}
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    autoComplete="name"
                    value={form.name}
                    onChange={(e) => setField('name', e.target.value)}
                    aria-invalid={Boolean(errors.name)}
                    aria-describedby={errors.name ? 'name-error' : undefined}
                  />
                  {errors.name ? (
                    <div id="name-error" className="text-sm text-destructive">
                      {errors.name}
                    </div>
                  ) : null}
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="email">
                    Email {requiredMark}
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    value={form.email}
                    onChange={(e) => setField('email', e.target.value)}
                    aria-invalid={Boolean(errors.email)}
                    aria-describedby={errors.email ? 'email-error' : undefined}
                  />
                  {errors.email ? (
                    <div id="email-error" className="text-sm text-destructive">
                      {errors.email}
                    </div>
                  ) : null}
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="company">Company</Label>
                  <Input
                    id="company"
                    name="company"
                    autoComplete="organization"
                    value={form.company ?? ''}
                    onChange={(e) => setField('company', e.target.value)}
                    aria-invalid={Boolean(errors.company)}
                    aria-describedby={errors.company ? 'company-error' : undefined}
                  />
                  {errors.company ? (
                    <div id="company-error" className="text-sm text-destructive">
                      {errors.company}
                    </div>
                  ) : null}
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="message">
                    Message {requiredMark}
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={form.message}
                    onChange={(e) => setField('message', e.target.value)}
                    aria-invalid={Boolean(errors.message)}
                    aria-describedby={errors.message ? 'message-error' : undefined}
                  />
                  {errors.message ? (
                    <div id="message-error" className="text-sm text-destructive">
                      {errors.message}
                    </div>
                  ) : null}
                </div>

                {status.type !== 'idle' ? (
                  <div
                    className={cn(
                      'rounded-md border px-3 py-2 text-sm',
                      status.type === 'success'
                        ? 'border-primary/30 bg-primary/5 text-foreground'
                        : 'border-destructive/30 bg-destructive/5 text-foreground'
                    )}
                    role="status"
                    aria-live="polite"
                  >
                    {status.message}
                  </div>
                ) : null}

                <Button type="submit" className="w-full" disabled={isSubmitting} aria-disabled={isSubmitting}>
                  {isSubmitting ? 'Sending…' : 'Submit'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </Container>
    </section>
  );
}

